import React, { PropsWithChildren, useEffect, useState } from 'react';
import { Button, CheckboxInput, INumberInputProps, Select } from 'ui-toolkit';
import { createOnChangeHandlerGenerator } from '../utilities/createOnChangeHandlerGenerator';
import CdNumberInput from '../CdNumberInput.styles';
import IHasItemDataProps from './IHasItemDataProps';
import IHasItemEventProps from './IHasItemEventProps';
import IItemProps from './IItemProps';
import styled from 'styled-components';
import {
  AlignedRow,
  BuyerBeforeContent,
  BuyerClosingContent,
  DeleteContent,
  ExtendedContent,
  LenderPaidContent,
  NameContent,
  NettedOutContent,
  OtherContent,
  SellerBeforeContent,
  SellerClosingContent,
  RowNumber,
} from './StyledCdItemCells';
import { lenderPaidOptions } from '../utilities/lenderPaidOptions';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faSort } from '@fortawesome/pro-solid-svg-icons/faSort';
import { StyledFontAwesomeThatCanBeDisabled } from '../../shared/permissions/StyledFontAwesomeThatCanBeDisabled';
import { generateDefaultViewItem } from '../../../ui-toolkit/cdItems/data/generateDefaultViewItem';
import { IBaseCDV3ViewItem } from '../../../ui-toolkit/cdItems/interfaces/ICDViewItem';
import { eCDV3ItemTypes } from '../../../ui-toolkit/enums/eCDV3ItemTypes';
import { eLenderPaid } from '../../../ui-toolkit/enums/eLenderPaid';
import { isNil } from 'lodash';

/**
 * Default values for `netted_out`. Can be evaluated with the following.
 *
 * ```typescript
 *
 * const item:IBaseCDV3ViewItem = getItemFromSomewhere();
 * const nettedOutDefault = cdV3ViewItemNettedOutDefaults[item.type] === true;
 *
 * ```
 * @type {{'[eCDV3ItemTypes.percentage_of_loan_amount]': boolean, '[eCDV3ItemTypes.escrow_at_closing]': boolean, '[eCDV3ItemTypes.escrow_at_closing_other]': boolean, '[eCDV3ItemTypes.origination_fee_other]': boolean}}
 */
export const nettedOutDefaults: Record<eCDV3ItemTypes, boolean> = {
  aggregate_adjustment: false,
  appraisal_fee: false,
  buyer_credit_from_seller: false,
  buyer_credit_from_third_party: false,
  buyer_deposit: false,
  buyer_reimbursement_from_seller: false,
  buyer_reimbursement_other_from_seller: false,
  buyer_reimbursement_other_to_seller: false,
  buyer_reimbursement_to_seller: false,
  buyer_reimbursement_to_third_party: false,
  endorsement: false,
  endorsement_other: false,
  escrow_at_closing: true,
  escrow_at_closing_other: true,
  existing_loans_assumed: false,
  hoa_fee: false,
  interest_prepayment: false,
  lender_credit: false,
  loan_amount: false,
  non_interest_prepayment: false,
  origination_fee_other: true,
  other_fee: false,
  other_loan_cost: false,
  payoff: false,
  percentage_of_loan_amount: true,
  personal_property_sale: false,
  prepayment_other: false,
  property_taxes: false,
  recording_fee: false,
  recording_fee_other: false,
  sale_price_of_property: false,
  sales_tax: false,
  seller_credit: false,
  seller_excess_deposit: false,
  tax: false,
  tax_other: false,
  title_fee: false,
  title_fee_other: false,
};

const MarginFixButton = styled(Button)`
  margin: 0 !important;
`;

const FullWidthSelect = styled(Select)`
  width: 100%;
`;

export enum SharedItemCellsActiveInput {
  BuyerAtClosing,
  BuyerBeforeClosing,
  SellerAtClosing,
  SellerBeforeClosing,
  PaidByOthers,
}

export interface ISharedItemCellsProps<T> extends IItemProps, IHasItemDataProps<T>, IHasItemEventProps<T> {
  inputDisplayOptions?: INumberInputProps;
  activeInput?: SharedItemCellsActiveInput | null;
  buyerBeforeClosingDisplay?: 'empty' | 'input';
  lenderPaid?: eLenderPaid;
  nettedOutDisplay?: 'empty' | 'input' | 'none';
  noLeftPadding?: boolean;
  noRightPadding?: boolean;
  onFocus?: () => void;
  onBlur?: (event: React.FocusEvent) => void;
  paidByOthersDisplay?: 'empty' | 'input';
  readOnly?: boolean;
  rowNumberDisplay?: 'empty' | 'none' | 'number';
  sellerAtClosingDisplay?: 'empty' | 'input';
  sellerBeforeClosingDisplay?: 'empty' | 'input';
  showLenderPaid?: boolean;
  extendedContent?: React.ReactNode;
  allowNegative?: boolean;
}

const SharedItemCells = <T extends IBaseCDV3ViewItem>({
  activeInput,
  children,
  data,
  extendedContent,
  noLeftPadding,
  noRightPadding,
  readOnly,
  onBlur,
  onChange,
  onFocus,
  onMove,
  onRemove,
  showLenderPaid,
  showRemove,
  showSeller,
  showMove,
  ...props
}: PropsWithChildren<ISharedItemCellsProps<T>>) => {
  const buyerBeforeClosingDisplay = props.buyerBeforeClosingDisplay || 'input';
  const nettedOutDisplay = props.nettedOutDisplay || 'input';
  const paidByOthersDisplay = props.paidByOthersDisplay || 'input';
  const rowNumberDisplay = props.rowNumberDisplay || 'number';
  const sellerAtClosingDisplay = props.sellerAtClosingDisplay || 'input';
  const sellerBeforeClosingDisplay = props.sellerBeforeClosingDisplay || 'input';

  const [buyerAtClosingInputNode, setBuyerAtClosingInputNode] = useState<HTMLInputElement | null>(null); // this will maybe be a useRef

  useEffect(() => {
    if (activeInput !== undefined && activeInput !== null) {
      let target = null;
      switch (activeInput) {
        case SharedItemCellsActiveInput.BuyerAtClosing:
          target = buyerAtClosingInputNode;
          break;
        case SharedItemCellsActiveInput.BuyerBeforeClosing:
        case SharedItemCellsActiveInput.SellerAtClosing:
        case SharedItemCellsActiveInput.SellerBeforeClosing:
        case SharedItemCellsActiveInput.PaidByOthers:
        default:
          throw new Error(
            `NOT IMPLEMENTED: SharedItemCells does not implement activeInput set to "${activeInput}". It is not fully implemented.`
          );
      }
      if (target) {
        target.focus();
      }
    }
  }, [activeInput, buyerAtClosingInputNode]);

  const inputDisplayOptions = Object.assign(
    {
      onBlur: onBlur,
      onFocus: onFocus,
    },
    props.inputDisplayOptions || {},
    readOnly !== undefined ? { readOnly } : {}
  );

  const generateDefaultOnChangeHandler = createOnChangeHandlerGenerator(data, onChange);
  const onSellerAtClosingChangeEvent = generateDefaultOnChangeHandler('seller_at_closing');
  const onSellerBeforeClosingChangeEvent = generateDefaultOnChangeHandler('seller_before_closing');
  const onPaidByOthersChangeEvent = (value: number | null) =>
    onChange({
      ...generateDefaultViewItem(),
      ...data,
      paid_by_others: value,
      lender_paid: value ? data.lender_paid : eLenderPaid.OtherNotSending,
      dirty: true,
    });

  const onNettedOutChange = (checked: boolean) =>
    onChange({ ...generateDefaultViewItem(), ...data, netted_out: checked, dirty: true });
  const onLenderPaidChange = (lenderPaid: { value: eLenderPaid; label: string }) => {
    onChange({ ...generateDefaultViewItem(), ...data, lender_paid: lenderPaid.value, dirty: true });
  };
  const onRemoveHandler = () => {
    if (onRemove) onRemove(data);
  };
  const onMoveHandler = () => {
    if (onMove) onMove(data);
  };
  const {
    buyer_at_closing,
    buyer_before_closing,
    lender_paid,
    netted_out,
    paid_by_others,
    seller_at_closing,
    seller_before_closing,
  } = data;
  const renderRowNumber =
    rowNumberDisplay === undefined || rowNumberDisplay === 'empty' || rowNumberDisplay === 'number';
  const hasBorrowerPaidFee = !isNil(buyer_at_closing) || !isNil(buyer_before_closing);
  // MF-422 Only show netted out IF one of the borrower paid columns has a value.
  const displayNettedOut = nettedOutDisplay === 'input' && hasBorrowerPaidFee;

  const handleUpdateBorrowerColumn = (
    buyerPropName: keyof Pick<IBaseCDV3ViewItem, 'buyer_before_closing' | 'buyer_at_closing'>,
    buyerPropValue: number | null
  ) => {
    if (hasBorrowerPaidFee && isNil(netted_out)) {
      // Handle edge case for old version of UI or data changed outside of this UI component.
      onChange({
        ...generateDefaultViewItem(),
        ...data,
        netted_out: nettedOutDefaults[data.type],
        [buyerPropName]: buyerPropValue,
        dirty: true,
      });
    } else if (isNil(buyerPropValue) && !hasBorrowerPaidFee) {
      // User cleared the value AND the sibling borrower paid column is also unset
      onChange({
        ...generateDefaultViewItem(),
        ...data,
        netted_out: undefined,
        [buyerPropName]: buyerPropValue,
        dirty: true,
      });
    } else if (!isNil(buyerPropValue) && !hasBorrowerPaidFee) {
      // 1st update to one of the borrower paid columns (previous value is not set but event had a value)
      onChange({
        ...generateDefaultViewItem(),
        ...data,
        netted_out: nettedOutDefaults[data.type],
        [buyerPropName]: buyerPropValue,
        dirty: true,
      });
    } else {
      // Do not update netted out
      onChange({
        ...generateDefaultViewItem(),
        ...data,
        [buyerPropName]: buyerPropValue,
        dirty: true,
      });
    }
  };
  const onBuyerAtClosingChangeEvent = (buyerAtClosing: number | null) =>
    handleUpdateBorrowerColumn('buyer_at_closing', buyerAtClosing);
  const onBuyerBeforeClosingChangeEvent = (buyerBeforeClosing: number | null) =>
    handleUpdateBorrowerColumn('buyer_before_closing', buyerBeforeClosing);

  return (
    <AlignedRow
      showSeller={showSeller}
      showLenderPaid={showLenderPaid !== false}
      showNettedOut={nettedOutDisplay !== 'none'}
      extendedContent={extendedContent}
    >
      {renderRowNumber && <RowNumber rowNumberDisplay={rowNumberDisplay} />}
      <NameContent noLeftPadding={noLeftPadding} noRightPadding={noRightPadding} children={children} />
      <BuyerClosingContent extendedContent={extendedContent}>
        <CdNumberInput
          mode={'currency'}
          {...inputDisplayOptions}
          getInputRef={setBuyerAtClosingInputNode}
          name="buyer_at_closing"
          onChange={onBuyerAtClosingChangeEvent}
          value={buyer_at_closing}
          readOnly={readOnly}
          debounceOnValueChange
        />
      </BuyerClosingContent>
      <BuyerBeforeContent extendedContent={extendedContent}>
        {/* TODO: Add support for text display type to all 5 amount columns */}
        {buyerBeforeClosingDisplay === 'input' ? (
          <CdNumberInput
            mode={'currency'}
            {...inputDisplayOptions}
            name="buyer_before_closing"
            onChange={onBuyerBeforeClosingChangeEvent}
            value={buyer_before_closing}
            readOnly={readOnly}
            debounceOnValueChange
          />
        ) : null}
      </BuyerBeforeContent>
      {showSeller ? (
        <SellerClosingContent extendedContent={extendedContent}>
          {sellerAtClosingDisplay === 'input' ? (
            <CdNumberInput
              mode={'currency'}
              {...inputDisplayOptions}
              name="seller_at_closing"
              onChange={onSellerAtClosingChangeEvent}
              value={seller_at_closing}
              readOnly={readOnly}
              debounceOnValueChange
            />
          ) : null}
        </SellerClosingContent>
      ) : null}
      {showSeller ? (
        <SellerBeforeContent extendedContent={extendedContent}>
          {sellerBeforeClosingDisplay === 'input' ? (
            <CdNumberInput
              mode={'currency'}
              {...inputDisplayOptions}
              name="seller_before_closing"
              onChange={onSellerBeforeClosingChangeEvent}
              value={seller_before_closing}
              readOnly={readOnly}
              debounceOnValueChange
            />
          ) : null}
        </SellerBeforeContent>
      ) : null}
      <OtherContent extendedContent={extendedContent}>
        {paidByOthersDisplay === 'input' ? (
          <CdNumberInput
            mode={'currency'}
            {...inputDisplayOptions}
            name="paid_by_others"
            onChange={onPaidByOthersChangeEvent}
            value={paid_by_others}
            readOnly={readOnly}
            debounceOnValueChange
          />
        ) : null}
      </OtherContent>
      <NettedOutContent extendedContent={extendedContent}>
        {displayNettedOut ? (
          <CheckboxInput name="netted_out" onChange={onNettedOutChange} value={netted_out} readOnly={readOnly} />
        ) : null}
      </NettedOutContent>
      <LenderPaidContent extendedContent={extendedContent}>
        {showLenderPaid !== false && !!paid_by_others && (
          <FullWidthSelect
            options={lenderPaidOptions()}
            name="lender_paid"
            onChange={onLenderPaidChange}
            defaultValue={lenderPaidOptions().find((option) => option.value === eLenderPaid.OtherNotSending)}
            value={lenderPaidOptions().find((option) => option.value === lender_paid)}
            isDisabled={readOnly}
          />
        )}
      </LenderPaidContent>
      <DeleteContent extendedContent={extendedContent}>
        {showRemove || showRemove === undefined ? (
          <MarginFixButton
            data-test-id={`delete-${data.id}`}
            intent="danger"
            isInline
            onClick={onRemoveHandler}
            disabled={readOnly}
          >
            <StyledFontAwesomeThatCanBeDisabled icon={faTrash} disabled={readOnly} />
          </MarginFixButton>
        ) : null}
        {showMove ? (
          <MarginFixButton intent="ghost" isInline onClick={onMoveHandler} disabled={readOnly}>
            <StyledFontAwesomeThatCanBeDisabled icon={faSort} disabled={readOnly} />
          </MarginFixButton>
        ) : null}
      </DeleteContent>
      {extendedContent && <ExtendedContent extendedContent={extendedContent}>{extendedContent}</ExtendedContent>}
    </AlignedRow>
  );
};

export default SharedItemCells;
